import { useContext, useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import DashboardLayout from "parts/LayoutContainers/DashboardLayout";
import DashboardNavbar from "parts/Navbars/DashboardNavbar";
import Footer from "parts/Footer";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDPagination from "components/MDPagination";
import MDButton from "components/MDButton";

import { useSessionContext } from "context/SessionContext";
import { LanguageContext } from "context";
import { useNotification } from "context/NotificationContext";

import RadarChart from "parts/Charts/RadarChart";

import { getPartners, saveSelectedPartner, type Partner } from "@shared/api/partner";

function Partner() {
  const { session } = useSessionContext();
  const { t } = useContext(LanguageContext) as any;
  const { showNotification } = useNotification();

  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogPartner, setDialogPartner] = useState<Partner | null>(null);

  const PARTNERS_PER_PAGE = 20;
  const pageCount = Math.ceil((partners?.length ?? 0) / PARTNERS_PER_PAGE);
  const pagedPartners = partners.slice((page - 1) * PARTNERS_PER_PAGE, page * PARTNERS_PER_PAGE);

  useEffect(() => {
    const fetchPartners = async () => {
    try {
        if (!session?.access_token) return;
        const data = await getPartners(session.access_token);
        if (data?.partners) {
          setPartners(data.partners);
          setSelectedPartnerId(data.selectedPartnerId);
        } else {
          showNotification(
          {
            icon: "error",
            title: t("Common.error"),
            message: t("Common.get-error"),
          },
          3000
        );
      }
    } catch (error) {
        console.error("仲間データの取得失敗", error);
        showNotification(
          {
            icon: "error",
            title: t("Common.error"),
            message: t("Common.get-error"),
          },
          3000
        );
      }
    };
    fetchPartners();
  }, [session?.access_token]);

  const handleSave = async () => {
    if (!selectedPartnerId) {
      console.log(`selectedPartnerId is empty`);
      return;
    }

    try {
      const res = await saveSelectedPartner(session.access_token, selectedPartnerId);
      if (res.success) {
        showNotification(
          {
            icon: "info",
            title: t("Common.success"),
            message: t("Common.updated-success"),
          },
          3000
        );
      }
    } catch (error) {
      console.error("保存エラー", error);
      showNotification(
        {
          icon: "error",
          title: t("Common.error"),
          message: t("Common.updated-error"),
        },
        3000
      );
    }
  };

  const selectedPartner = partners.find((p) => p.id === selectedPartnerId);

  const handleDetailClick = (partner: Partner) => {
    setDialogPartner(partner);
    setDialogOpen(true);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDTypography variant="h4" mb={2}>
          {t("patner.partner")}
        </MDTypography>

        {/* 選択中仲間 */}
        <MDBox display="flex" justifyContent="center" mb={3}>
          <Card sx={{ p: 2, maxWidth: 400, width: "100%" }}>
            <MDTypography variant="h6" gutterBottom>
              {t("patner.select")}
            </MDTypography>
            {selectedPartner && (
              <MDBox display="flex" alignItems="center" flexDirection="column" gap={2}>
                <MDAvatar src={selectedPartner.image_url} alt={selectedPartner.title} size="xxl"
                  sx={{
                    width: 220,
                    height: 220,
                  }} />
                <MDTypography variant="button">{selectedPartner.title}</MDTypography>
                <MDBox mt={1}>
                  <MDButton variant="gradient" color="info" onClick={handleSave}>
                    {t("patner.save")}
                  </MDButton>
                </MDBox>
              </MDBox>
            )}
          </Card>
        </MDBox>

        <Divider />

        {/* 仲間選択 */}
        <Card sx={{ p: 2, mb: 3 }}>
          <MDTypography variant="h6" gutterBottom>
            {t("patner.select-partner")}
          </MDTypography>

          <MDBox display="flex" flexWrap="wrap" justifyContent="center" gap={3}>
            {pagedPartners.map((partner) => (
              <MDBox key={partner.id} display="flex" flexDirection="column" alignItems="center">
                <MDAvatar
                  src={partner.image_url}
                  alt={partner.title}
                  size="xxl"
                  sx={{
                    cursor: "pointer",
                    border: selectedPartnerId === partner.id ? "3px solid #2196f3" : "none",
                    width: 128,
                    height: 128,
                  }}
                  onClick={() => setSelectedPartnerId(partner.id)}
                />
                <MDTypography variant="caption" mt={1} mb={1}>
                  {partner.title}
                </MDTypography>
                
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={() => handleDetailClick(partner)}
                  sx={{ minWidth: "60px", padding: "4px 8px" }}
                >
                  {t("patner.detail")}
                </MDButton>
              </MDBox>
            ))}
          </MDBox>

          {pageCount > 1 && (
            <MDBox mt={3} display="flex" justifyContent="center">
              <MDPagination
                active={page}
                size="small"
                total={pageCount}
                onChange={(_: any, value: number) => setPage(value)}
              />
            </MDBox>
          )}
        </Card>
      </MDBox>
      <Footer />

      {/* 詳細ダイアログ表示 */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          {dialogPartner && (
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              textAlign="center"
              gap={2}
            >
              <MDAvatar src={dialogPartner.image_url} alt={dialogPartner.title} size="xxl" />
              <MDTypography variant="h6">{dialogPartner.title}</MDTypography>
              <MDTypography variant="body2">{dialogPartner.detail}</MDTypography>

              <RadarChart
                title={t("patner.personality")}
                chart={{
                  labels: [t("patner.kindness"), t("patner.intelligence"), t("patner.cooperativeness"),
                           t("patner.meticulousness"), t("patner.originality"), t("patner.toughness")],
                  datasets: [
                    {
                      label: t("patner.score"),
                      color: "warning",
                      data: [
                        dialogPartner.kindness,
                        dialogPartner.intelligence,
                        dialogPartner.cooperation,
                        dialogPartner.punctuality,
                        dialogPartner.originality,
                        dialogPartner.toughness,
                      ],
                    },
                  ],
                }}
              />
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant="gradient" color="info" onClick={() => setDialogOpen(false)}>
            {t("patner.close")}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Partner;